'use client';

import {
  useIsFeatureEnabled,
  useIsFeatureEnabledResolved,
} from '@lib/observability/feature-flags/use-is-feature-enabled';

const FLAG = 'enable-clerk-kill-switch';

// Emergency off-switch, default OFF. If PostHog never settles the flag
// (slow, blocked, or undefined in the project), fail open after this wait:
// read off + resolved so resolution-gated callers (auto-sign-in entry,
// OAuth callbacks) proceed instead of hanging. The bounded wait still
// guards the first-render race the resolved variant exists for.
const KILL_SWITCH_RESOLVE_TIMEOUT_MS = 3000;

/**
 * True when the PostHog `enable-clerk-kill-switch` flag is on.
 * Callers short-circuit anonymous Clerk auth surfaces to
 * `<AuthUnavailable/>` while the flag is active.
 *
 * This boolean variant returns `false` while the PostHog provider
 * is still mounting (hydration-consistent with the server render).
 * Pure render gates can use it directly; callers that initiate
 * side effects (OAuth redirects, `signIn.create`) should use the
 * resolved variant below to avoid a first-render race where the
 * side effect fires before the flag value settles.
 */
export function useClerkKillSwitch(): boolean {
  return useIsFeatureEnabled(FLAG);
}

/**
 * Tri-state variant: `{active, isResolved}`. `active` is true only
 * when the flag is on AND PostHog has resolved. `isResolved` flips
 * true once the provider has settled on the client.
 *
 * Side-effect callers (OAuth callback pages, auto-sign-in entry
 * effects) should gate on `isResolved` first — render a neutral
 * loading state while the flag value is unknown, then either fire
 * the side effect (when resolved and not active) or render
 * maintenance (when resolved and active).
 *
 * The race this guards against: PostHog's hook returns `false` on
 * first render via a mount-state guard (for hydration consistency),
 * so a boolean read of the flag on first effect run is always
 * `false` — letting a side effect fire before the real flag value
 * is available.
 */
export function useClerkKillSwitchResolved(): {
  active: boolean;
  isResolved: boolean;
} {
  const { enabled, isResolved } = useIsFeatureEnabledResolved(FLAG, {
    failOpenAfterMs: KILL_SWITCH_RESOLVE_TIMEOUT_MS,
  });
  return { active: enabled, isResolved };
}
